import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Printer, Activity } from 'lucide-react';
import { formatCATDateTime } from '../utils/timezone';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ITEMS_PER_PAGE = 18; // Show 18 items per page

const PrintBill = () => {
  const { claimId } = useParams();
  const [claimData, setClaimData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBillData();
  }, [claimId]);

  const loadBillData = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      window.location.href = '/login';
      return;
    }

    try {
      const response = await axios.get(`${API}/claims/${claimId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClaimData(response.data);
    } catch (error) {
      alert('Failed to load claim data');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-gray-600">Loading claim...</div>
      </div>
    );
  }

  if (!claimData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-gray-600">Claim not found</div>
      </div>
    );
  }

  const { header, details, currency } = claimData;
  
  // Format currency with thousand separators
  const formatAmount = (amountUSD) => {
    const rate = currency?.rate_to_usd || 1.0;
    const decimals = currency?.decimal_places || 2;
    const symbol = currency?.symbol || '$';
    
    // Convert from USD to local currency
    const amountLocal = amountUSD * rate;
    
    // Format with thousand separators
    const formatted = amountLocal.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
    
    return `${symbol}${formatted}`;
  };

  // Group items into sections when categories are available (Services, Lab, Radiology, Medications)
  const CATEGORY_LABELS = { service: 'Services / Fees', lab: 'Laboratory Tests', radiology: 'Radiology Scans', dental: 'Dental Investigations', medication: 'Medications' };
  const buildRows = (items) => {
    if (!items.some((d) => d.category)) return items;
    const rows = [];
    ['service', 'lab', 'radiology', 'dental', 'medication', ''].forEach((cat) => {
      const group = items.filter((d) => (d.category || '') === cat);
      if (group.length > 0) {
        rows.push({ __header: CATEGORY_LABELS[cat] || 'Other Items' });
        rows.push(...group);
      }
    });
    return rows;
  };

  // Split items into pages of 12 items each
  const paginateItems = (items) => {
    const pages = [];
    const rows = buildRows(items);
    for (let i = 0; i < rows.length; i += ITEMS_PER_PAGE) {
      pages.push(rows.slice(i, i + ITEMS_PER_PAGE));
    }
    return pages;
  };

  const itemPages = paginateItems(details);
  const totalPages = itemPages.length;

  // Render page header (used on each page)
  const renderPageHeader = (pageNumber) => (
    <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-gray-400 print:border-b print:border-gray-400">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="bg-gray-400 p-2 rounded-lg print:bg-white print:border print:border-gray-400">
            <Activity className="w-6 h-6 text-white print:text-gray-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 print:text-gray-700">Acacia Insurance Claim</h1>
        </div>
        <p className="text-lg text-gray-700 font-medium print:text-gray-600">{header.hospital_name}</p>
      </div>
      <div className="text-right">
        <div className="text-sm text-gray-600 font-medium">Claim ID</div>
        <div className="text-xl font-bold text-gray-800 print:text-gray-700" data-testid="bill-id">{header.claim_id}</div>
        {header.preauth_number && (
          <div className="text-sm text-gray-700 mt-1">Pre-auth #: <span className="font-bold" data-testid="bill-preauth">{header.preauth_number}</span></div>
        )}
        {header.doctor_name && (
          <div className="text-sm text-gray-700 mt-1">Attending doctor: <span className="font-bold" data-testid="bill-doctor">{header.doctor_name}</span></div>
        )}
        <div className="text-sm text-gray-600 mt-2">{formatCATDateTime(header.timestamp)}</div>
        {totalPages > 1 && (
          <div className="text-xs text-gray-500 mt-1">Page {pageNumber} of {totalPages}</div>
        )}
      </div>
    </div>
  );

  // Render patient info (only on first page)
  const renderPatientInfo = () => (
    <div className="mb-6 bg-gray-50 p-6 rounded-lg border border-gray-300 print:bg-white print:border print:border-gray-400">
      <h2 className="text-lg font-bold text-gray-700 mb-4 uppercase print:text-gray-600">Patient Information</h2>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <span className="text-sm text-gray-600 font-medium">Full Name:</span>
          <div className="font-bold text-gray-800 text-base print:text-gray-700" data-testid="bill-patient-name">{header.patient_name}</div>
        </div>
        <div>
          <span className="text-sm text-gray-600 font-medium">Age:</span>
          <div className="font-bold text-gray-800 print:text-gray-700">{header.patient_age ? `${header.patient_age} years` : 'N/A'}</div>
        </div>
        <div>
          <span className="text-sm text-gray-600 font-medium">Sex:</span>
          <div className="font-bold text-gray-800 print:text-gray-700">{header.patient_sex || 'N/A'}</div>
        </div>
        <div>
          <span className="text-sm text-gray-600 font-medium">Serial Number:</span>
          <div className="font-bold text-gray-800 print:text-gray-700">{header.patient_serial_number}</div>
        </div>
        <div>
          <span className="text-sm text-gray-600 font-medium">Family ID:</span>
          <div className="font-bold text-gray-800 print:text-gray-700">{header.family_id}</div>
        </div>
        <div>
          <span className="text-sm text-gray-600 font-medium">Relationship:</span>
          <div className="font-bold text-gray-800 print:text-gray-700">{(header.patient_relationship === 'Principle' ? 'Principal' : header.patient_relationship) || 'N/A'}</div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <style>{`
        @media print {
          .page-break {
            page-break-after: always;
            break-after: page;
          }
          .no-break {
            page-break-inside: avoid;
            break-inside: avoid;
          }
          @page {
            margin: 1cm;
          }
        }
      `}</style>
      
      <div className="min-h-screen bg-gray-50 p-8 print:p-0 print:bg-white">
        {/* Print Button - Only shown on screen, not in print */}
        <div className="max-w-4xl mx-auto mb-4 print:hidden">
          <div className="bg-white shadow-lg rounded-lg p-4 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              <strong>Total Items:</strong> {details.length} | <strong>Pages:</strong> {totalPages}
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handlePrint}
                className="bg-gray-600 text-white hover:bg-gray-700 font-semibold"
                data-testid="print-button"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print Claim
              </Button>
              <Button
                onClick={() => window.close()}
                variant="outline"
                className="bg-white text-gray-700 hover:bg-gray-100 font-semibold border-2 border-gray-300"
              >
                Close
              </Button>
            </div>
          </div>
        </div>

        {/* Render each page */}
        {itemPages.map((pageItems, pageIndex) => {
          const isFirstPage = pageIndex === 0;
          const isLastPage = pageIndex === itemPages.length - 1;
          const pageNumber = pageIndex + 1;

          return (
            <div 
              key={`bill-page-${pageIndex}`} 
              className={`max-w-4xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden mb-8 print:shadow-none print:rounded-none print:mb-0 ${!isLastPage ? 'page-break' : ''}`}
            >
              <div className="p-8 print:p-6" data-testid={`print-bill-content-page-${pageNumber}`}>
                {/* Page Header */}
                {renderPageHeader(pageNumber)}

                {/* Patient Info - Only on first page */}
                {isFirstPage && renderPatientInfo()}

                {/* Bill Items for this page */}
                <div className="mb-6">
                  {isFirstPage && (
                    <h2 className="text-lg font-bold text-gray-700 mb-4 uppercase print:text-gray-600">Services & Medications</h2>
                  )}
                  
                  <table className="w-full border border-gray-400 print:border print:border-gray-400">
                    <thead className="bg-gray-100 print:bg-white">
                      <tr>
                        <th className="text-left p-3 border-b border-gray-400 font-bold text-gray-700 print:text-gray-600 print:border-b print:border-gray-400">Item Name</th>
                        <th className="text-left p-3 border-b border-gray-400 font-bold text-gray-700 print:text-gray-600 print:border-b print:border-gray-400">UoM</th>
                        <th className="text-right p-3 border-b border-gray-400 font-bold text-gray-700 print:text-gray-600 print:border-b print:border-gray-400">Unit Cost</th>
                        <th className="text-center p-3 border-b border-gray-400 font-bold text-gray-700 print:text-gray-600 print:border-b print:border-gray-400">Qty</th>
                        <th className="text-right p-3 border-b border-gray-400 font-bold text-gray-700 print:text-gray-600 print:border-b print:border-gray-400">Total</th>
                      </tr>
                    </thead>
                    <tbody data-testid="bill-items-list">
                      {pageItems.map((item, index) => (
                        item.__header ? (
                          <tr key={`hdr-${item.__header}-${index}`} className="border-b border-gray-300 print:border-b print:border-gray-300">
                            <td colSpan="5" className="p-2 pl-3 bg-gray-50 print:bg-white font-bold text-gray-600 uppercase text-xs tracking-wide" data-testid={`bill-section-${item.__header.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                              {item.__header}
                            </td>
                          </tr>
                        ) : (
                        <tr key={`${item.item_id || item.item_name}-${index}`} className="border-b border-gray-300 print:border-b print:border-gray-300">
                          <td className="p-3 text-gray-800 font-medium print:text-gray-700">{item.item_name}</td>
                          <td className="p-3 text-gray-700 print:text-gray-600">{item.uom || '-'}</td>
                          <td className="p-3 text-right text-gray-700 print:text-gray-600">{formatAmount(item.item_cost)}</td>
                          <td className="p-3 text-center font-bold text-gray-800 print:text-gray-700">{item.quantity || 1}</td>
                          <td className="p-3 text-right font-bold text-gray-800 print:text-gray-700">{formatAmount(item.item_cost * (item.quantity || 1))}</td>
                        </tr>
                        )
                      ))}
                    </tbody>
                    
                    {/* Show total only on last page */}
                    {isLastPage && (
                      <tfoot className="bg-gray-50 print:bg-white">
                        <tr className="border-t border-gray-500 print:border-t print:border-gray-500">
                          <td colSpan="3" className="p-4 font-bold text-gray-800 text-lg uppercase print:text-gray-700">Total Amount</td>
                          <td className="p-4 text-right font-bold text-gray-900 text-2xl print:text-gray-800" data-testid="bill-total">{formatAmount(header.total_claim_amount)}</td>
                        </tr>
                      </tfoot>
                    )}
                  </table>

                  {/* Page indicator for multi-page claims */}
                  {!isLastPage && totalPages > 1 && (
                    <div className="text-right text-sm text-gray-500 mt-2 print:text-gray-600">
                      Continued on next page...
                    </div>
                  )}
                </div>

                {/* Footer - Only on last page */}
                {isLastPage && (
                  <div className="text-center text-sm text-gray-600 pt-6 border-t border-gray-300 print:border-t print:border-gray-400">
                    <p className="font-medium print:text-gray-600">This is a computer-generated claim document and does not require a signature.</p>
                    <p className="mt-2 print:text-gray-600">Thank you for choosing {header.hospital_name}</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default PrintBill;